import { useEffect, useRef, useState } from "react";
import { ReactComponent as PendingIcon } from "@src/assets/images/fide/pending.svg";
import { ReactComponent as AcceptSystemIcon } from "@src/assets/images/fide/acceptSystem.svg";
import { ReactComponent as AcceptAdminIcon } from "@src/assets/images/fide/acceptAdmin.svg";
import { ReactComponent as DeclineFideIcon } from "@src/assets/images/fide/declineFide.svg";
import "@styles/react/libs/flatpickr/flatpickr.scss";
import FileUploaderSingle from "@components/file-uploader";
import "@styles/react/libs/file-uploader/file-uploader.scss";
import {
  Button,
  Card,
  Col,
  FormFeedback,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import "@styles/react/pages/page-profile.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  CompleteProfileModal,
  DownloadPassportButton,
  useGetProfile,
  useUpdateFideVerification,
  useUpdateProfile,
} from "@features/user";
import { formatImageSrc } from "@utils";
import { handleUpdateUser } from "@store/authentication";
import { Controller, useForm } from "react-hook-form";
import defaultAvatar from "@src/assets/images/portrait/small/avatar-s-11.jpg";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const validationSchema = z.object({
  passport: z
    .instanceof(File, { message: "Please upload an Image" })
    .refine(
      (file) => file.size < 5 * 1024 * 1024,
      "The selected file is too large. Please upload a file smaller than 5MB."
    )
    .refine(
      (file) => ["image/jpeg", "image/png"].includes(file.type),
      "The selected file is not valid. Please upload only image files in .jpg, .png, or .jpeg formats."
    ),
});

export const Fide = () => {
  const [completeProfileModal, setCompleteProfileModal] = useState(false);
  const fideRefTimeout = useRef(null);
  const dispatch = useDispatch();
  const authStore = useSelector((state) => state.auth);
  const user = authStore.userData;

  const isProfileIncomplete =
    !user?.profile ||
    !user?.first_name ||
    !user?.last_name ||
    !user?.profile?.birthday ||
    !user?.profile?.gender ||
    !user?.profile?.continent_id ||
    !user?.profile?.state_id ||
    !user?.profile?.city_id ||
    !user?.profile?.federation_id;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const updateFideMutation = useUpdateFideVerification({
    config: {
      onSuccess: (data) => {
        dispatch(handleUpdateUser(data.data));
        profileQuery.refetch();
      },
    },
  });

  const updateProfileMutation = useUpdateProfile({
    config: {
      onSuccess: (data) => {
        dispatch(handleUpdateUser(data.data));
        profileQuery.refetch();
      },
    },
  });

  const profileQuery = useGetProfile({});

  useEffect(() => {
    if (profileQuery.isSuccess) {
      const profile = profileQuery.data.data.profile;
      setValue("fideId", profile?.fide_id);
      setValue("rapid", profile?.fide_RRtng);
      setValue("blitz", profile?.fide_BRtng);
      setValue("std", profile?.fide_SRtng);
      setValue("fideTitle", profile?.fide_Tit);
      setValue("otherTitle", profile?.fide_OTit);
      setValue("flag", profile?.fide_Flag);
      setValue("federation", profile?.fide_Fed);
      setValue("fideName", profile?.fide_Name);
    }
  }, [profileQuery.isSuccess, profileQuery.data]);

  const handleOnchangeFideId = (e, onChange) => {
    if (isProfileIncomplete) return setCompleteProfileModal(true);

    clearTimeout(fideRefTimeout.current);
    onChange(e);
    if (e.target.value.length >= 5) {
      fideRefTimeout.current = setTimeout(() => {
        updateFideMutation.mutateAsync({ data: { fide_id: e.target.value } });
        // updateProfileMutation.mutateAsync({
        //   data: {
        //     fide_id: e.target.value,
        //     fide_verification: 1,
        //   },
        // });
      }, 1000);
    }
  };

  const onSubmit = (data) => {
    if (isProfileIncomplete) return setCompleteProfileModal(true);

    updateProfileMutation.mutateAsync({
      data: {
        passport_img: data.passport,
      },
    });
  };

  if (profileQuery.isLoading || profileQuery.isPending) {
    return (
      <div className="height-300 d-flex align-items-center justify-content-center">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!profileQuery.data || profileQuery.isError) {
    return <div>No Data ...</div>;
  }

  return (
    <Row>
      <Card className={"p-1 mb-1 flex-row justify-content-between"}>
        <div className={"d-flex align-items-center gap-1"}>
          <img
            src={
              profileQuery.data.data?.profile?.profile_img
                ? formatImageSrc(profileQuery.data.data.profile.profile_img)
                : defaultAvatar
            }
            className="rounded-circle width-60 height-60"
            alt="User image"
          />
          <div>
            <h2 className={"fw-bolder"}>Fide Id</h2>
            <h6>
              {profileQuery.data.data.first_name}{" "}
              {profileQuery.data.data.last_name}
            </h6>
          </div>
        </div>
        <div className={"d-flex align-items-center"}>
          {!profileQuery.data.data?.profile?.fide_id && (
            <div
              className={
                "d-flex align-items-center gap-50 bg-light-warning py-50 px-1 rounded-3"
              }
            >
              <PendingIcon height={24} width={24} />
              Pending Completion
            </div>
          )}
          {profileQuery.data.data?.profile?.fide_verification === 1 && (
            <div
              className={
                "d-flex align-items-center gap-50 bg-light-success py-75  justify-content-center rounded-3 px-1"
              }
            >
              <AcceptSystemIcon height={24} width={24} />
              Fide is verify by system
            </div>
          )}
          {profileQuery.data.data?.profile?.fide_verification === 2 && (
            <div
              className={
                "d-flex align-items-center gap-50 bg-light-info py-75  justify-content-center rounded-3 px-1"
              }
            >
              <AcceptAdminIcon height={24} width={24} />
              Fide is verify by admin
            </div>
          )}
          {profileQuery.data.data?.profile?.fide_verification === 0 &&
            profileQuery.data.data?.profile?.fide_id && (
              <div
                className={
                  "d-flex align-items-center gap-50 bg-light-danger py-75  justify-content-center rounded-3 px-1"
                }
              >
                <DeclineFideIcon height={24} width={24} />
                Fide is not Verify
              </div>
            )}
        </div>
      </Card>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={"d-flex p-0 mt-2"}>
          <Card className={" rounded-3 p-1 width-100 m-0 text-center"}>
            <span className={"text-white m-0"}>Info</span>
          </Card>
        </div>
        <Row className={"border rounded-3 mx-0 p-1 mt-75"}>
          <Col className={"p-0 pe-50"} xs={"6"}>
            <div className="form-floating">
              <Controller
                id="fideId"
                name="fideId"
                control={control}
                render={({ field }) => (
                  <Input
                    id="fideId"
                    size="lg"
                    disabled={
                      updateProfileMutation.isPending ||
                      profileQuery.isRefetching
                    }
                    placeholder=""
                    min={1}
                    max={99999999}
                    type="number"
                    readOnly={
                      profileQuery.data.data?.profile?.fide_verification === 2
                    }
                    {...field}
                    onChange={(v) => handleOnchangeFideId(v, field.onChange)}
                  />
                )}
              />
              <label htmlFor="fideId">FIDE ID</label>
            </div>
            {(updateProfileMutation.isPending ||
              profileQuery.isRefetching ||
              updateFideMutation.isPending) && (
              <div className="mt-25">
                <Spinner size="sm" />{" "}
                <small className="text-secondary ms-25">Loading</small>
              </div>
            )}
            {!updateFideMutation.isPending && updateFideMutation.isSuccess && (
              <p className="text-success"> &#10003; Success</p>
            )}
            {!updateFideMutation.isPending && updateFideMutation.isError && (
              <p className="text-danger"> &#88; Your Fide ID is wrong</p>
            )}
          </Col>
          <Col className={"p-0 ps-50"} xs={6}>
            <div className="form-floating ">
              <Controller
                id="federation"
                name="federation"
                control={control}
                render={({ field }) => (
                  <Input
                    id="federation"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="federation">Federation</label>
            </div>
          </Col>


          <Col className={"p-0 pe-50 mt-1"} xs={"6"}>
            <div className="form-floating">
              <Controller
                id="fideTitle"
                name="fideTitle"
                control={control}
                render={({ field }) => (
                  <Input
                    id="fideTitle"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="fideTitle">FIDE Title</label>
            </div>
          </Col>
          <Col className={"p-0 ps-50 mt-1"} xs={6}>
            <div className="form-floating">
              <Controller
                id="otherTitle"
                name="otherTitle"
                control={control}
                render={({ field }) => (
                  <Input
                    id="otherTitle"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />
              <label htmlFor="otherTitle">Other Title</label>
            </div>
          </Col>
          <Col className={"p-0 pe-50 mt-1"} xs={"6"}>
            <div className="form-floating ">
              <Controller
                  id="fideName"
                  name="fideName"
                  control={control}
                  render={({ field }) => (
                      <Input
                          id="fideName"
                          size="lg"
                          placeholder=""
                          readOnly
                          {...field}
                      />
                  )}
              />

              <label htmlFor="fideName">Fide Name</label>
            </div>
          </Col>
          <Col className={"p-0 ps-50 mt-1"} xs={"6"}>
            <div className="form-floating ">
              <Controller
                id="flag"
                name="flag"
                control={control}
                render={({ field }) => (
                  <Input
                    id="flag"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="flag">Inactive flag</label>
            </div>
          </Col>
        </Row>

        <div className={"d-flex p-0 mt-3"}>
          <Card className={" rounded-3 p-1 width-100 m-0 text-center"}>
            <span className={"text-white m-0"}>Rating</span>
          </Card>
        </div>
        <Row className={"border rounded-3 mx-0 p-1 mt-75 mb-2"}>
          <Col className={"p-0 pe-50"} xs={"6"}>
            <div className="form-floating">
              <Controller
                id="std"
                name="std"
                control={control}
                render={({ field }) => (
                  <Input
                    id="std"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="std">std</label>
            </div>
          </Col>
          <Col className={"p-0 ps-50"} xs={6}>
            <div className="form-floating w-100">
              <Controller
                id="rapid"
                name="rapid"
                control={control}
                render={({ field }) => (
                  <Input
                    id="rapid"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="rapid">rapid</label>
            </div>
          </Col>

          <Col className={"p-0 pe-50 mt-1"} xs={"6"}>
            <div className="form-floating w-100">
              <Controller
                id="blitz"
                name="blitz"
                control={control}
                render={({ field }) => (
                  <Input
                    id="blitz"
                    size="lg"
                    placeholder=""
                    readOnly
                    {...field}
                  />
                )}
              />

              <label htmlFor="blitz">blitz</label>
            </div>
          </Col>
        </Row>
        <Row className={"m-0 p-0"}>
          {!profileQuery.data.data?.profile?.passport_img && (
            <Col className={"my-2 p-0 position-relative"} xs={12}>
              <h5
                style={{ top: "10%", left: "2%" }}
                className={"position-absolute"}
              >
                Passport Upload
              </h5>
              <Controller
                name="passport"
                rules={{ required: true }}
                control={control}
                defaultValue={null} // Default value for the file field
                render={({ field: { value, onChange } }) => (
                  <FileUploaderSingle value={value} onChange={onChange} image />
                )}
              />
              {errors.passport && (
                <FormFeedback className="d-block mt-50">
                  {errors.passport.message}
                </FormFeedback>
              )}
            </Col>
          )}
          {profileQuery.data.data?.profile?.passport_img && (
            <DownloadPassportButton
              passport={profileQuery.data.data.profile.passport_img}
            />
          )}
          {(!profileQuery.data.data?.profile?.passport_img ||
            !profileQuery.data.data?.profile?.fide_id || profileQuery.data.data?.profile?.fide_verification === 0) && (
            <Button
              type="submit"
              color="primary"
              block
              size="lg"
              className={"mb-1"}
            >
              {updateProfileMutation.isPending ? (
                <Spinner />
              ) : (
                "Send for Verification"
              )}
            </Button>
          )}
        </Row>
      </form>
      <CompleteProfileModal
        completeProfileModal={completeProfileModal}
        setCompleteProfileModal={setCompleteProfileModal}
      />
    </Row>
  );
};
